import { useLayoutEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(-1 * var(--shift))); }
`

// The track is absolutely positioned so its (possibly very wide) content never contributes to
// the width of ancestors. In flow, it widened the panel -> text fit -> ticker off -> panel
// shrank -> ticker on... a resize feedback loop that flashed the whole page.
const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: 0;
  height: 1.3em;
  line-height: 1.3;
  white-space: nowrap;
`

const Track = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  width: max-content;
  will-change: transform;
  &.scrolling {
    animation: ${scroll} var(--duration) linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &.scrolling {
      animation: none;
    }
  }
`

const Copy = styled.span`
  flex: none;
`

const Gap = styled.span`
  flex: none;
  width: 4vw;
`

// Scrolls its text like a news ticker when it is wider than its container;
// short text is left static. `speed` is in px/second.
export const Marquee = ({ children, speed = 60, className }) => {
  const viewport = useRef()
  const copy = useRef()
  const track = useRef()
  const [overflow, setOverflow] = useState(false)
  const [shift, setShift] = useState(0)

  // 1. Does a single copy of the text fit?
  useLayoutEffect(() => {
    const measure = () => {
      if (!viewport.current || !copy.current) return
      setOverflow(copy.current.offsetWidth > viewport.current.clientWidth + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(viewport.current)
    ro.observe(copy.current)
    return () => ro.disconnect()
  }, [children])

  // 2. Once duplicated (copy, gap, copy, gap), scrolling half the track loops seamlessly
  useLayoutEffect(() => {
    if (overflow && track.current) setShift(track.current.scrollWidth / 2)
  }, [overflow, children])

  return (
    <Viewport ref={viewport} className={className} title={typeof children === 'string' ? children : undefined}>
      <Track
        ref={track}
        className={overflow && shift ? 'scrolling' : undefined}
        style={overflow && shift ? { '--shift': `${shift}px`, '--duration': `${shift / speed}s` } : undefined}
      >
        <Copy ref={copy}>{children}</Copy>
        {overflow && (
          <>
            <Gap />
            <Copy aria-hidden="true">{children}</Copy>
            <Gap />
          </>
        )}
      </Track>
    </Viewport>
  )
}
